import * as React from 'react'
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CalendarCheck,
  Check,
  ChevronDown,
  ClipboardList,
  Clock,
  Copy,
  IdCard,
  KeyRound,
  Mail,
  MapPin,
  Minus,
  Phone,
  RefreshCw,
  RotateCw,
  ScrollText,
  ShieldCheck,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { formatDob } from '@acs/shared'
import type {
  CustomerDetail,
  CustomerStatus,
  OtpFetchReason,
  OtpFetchResponse,
  SecuredShiftDto,
  ShiftPriority,
} from '@acs/shared'
import { ApiError, apiPatch } from '@/lib/api'
import { cn } from '@/lib/cn'
import { friendlyError } from '@/hooks/use-create-customer'
import { applyCustomerPatches, useCustomerDetail } from '@/hooks/use-customers'
import { useFetchOtp } from '@/hooks/use-otp'
import { useLiveElapsed, useRelativeTime } from '@/hooks/use-relative-time'
import { Badge, type Tone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton, writeClipboard } from '@/components/ui/copy-button'
import { Drawer } from '@/components/ui/drawer'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { Tooltip } from '@/components/ui/tooltip'
import { AssessmentCard, AssessmentDialog } from '@/components/customers/assessment-dialog'
import { ShiftPriorityPicker } from '@/components/customers/shift-priority'

/**
 * The customer inspector.
 *
 * Everything the table has to leave out lives here: the identity record an
 * operator reads a handover against, the address, the city → warehouse map and
 * the work-hour preferences.
 *
 * One rule governs the whole panel: of the four credentials on the source
 * document, `work_auth` and `gmail_imap_app_password` are shown as *presence*,
 * never as values — they are not projected out of Mongo at all, so no code
 * path here could leak one even if someone tried to add it. `pin` and
 * `sin_number` are the two deliberate reversals: this is the one screen where
 * a record is checked against what was typed in, so both are shown in the
 * clear, with the same copy affordance as every other value an operator pastes
 * elsewhere. The decision trail lives on `DETAIL_PROJECTION` in the API.
 */

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

const absoluteFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'medium',
})

function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return iso
  return absoluteFormat.format(ms)
}

/*
 * There is deliberately no date-only formatter here any more.
 *
 * The one that used to be — `Date.parse` into an unpinned `Intl.DateTimeFormat`
 * — existed solely for the date of birth, and it was the only place in this app
 * that turned a calendar day into an instant. A date-only string parses as
 * midnight UTC and then renders in the viewer's zone, so every operator west of
 * Greenwich read a birthday one day early. `formatDob` from `@acs/shared`
 * replaces it and cannot make that mistake; see the note there.
 */

const cityLabelCache = new Map<string, string>()
function formatCity(raw: string): string {
  const cached = cityLabelCache.get(raw)
  if (cached !== undefined) return cached
  const parts = raw.split(',')
  const head = (parts[0] ?? raw).trim()
  const tail = (parts[1] ?? '').trim()
  const city = head.replace(/(^|[\s-])([a-z])/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase())
  const label = tail === '' ? city : `${city}, ${tail.toUpperCase()}`
  cityLabelCache.set(raw, label)
  return label
}

/* ------------------------------------------------------------------ *
 * Layout primitives (module scope)
 * ------------------------------------------------------------------ */

function Section(p: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-b border-line px-4 py-3.5 last:border-b-0">
      <h3 className="mb-2.5 flex items-center gap-1.5 font-display text-[11px] font-semibold tracking-[0.07em] text-faint uppercase">
        {p.icon ? (
          <span aria-hidden="true" className="[&_svg]:size-3.5">
            {p.icon}
          </span>
        ) : null}
        {p.title}
      </h3>
      {p.children}
    </section>
  )
}

function Field(p: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={cn('min-w-0', p.wide && 'col-span-2')}>
      <dt className="text-[11px] text-faint">{p.label}</dt>
      <dd className="mt-px min-w-0 text-[12.5px] break-words text-fg">{p.children}</dd>
    </div>
  )
}

function Grid(p: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">{p.children}</dl>
}

function Blank() {
  return <span className="text-faint">—</span>
}

function Text(p: { value: string | null | undefined; mono?: boolean }) {
  if (!p.value) return <Blank />
  return <span className={cn(p.mono && 'tabular font-mono text-[12px]')}>{p.value}</span>
}

/**
 * The date of birth — the one field here that must never be *interpreted*.
 *
 * Three outcomes, kept apart deliberately. Nothing on file reads as "—", like
 * every other blank. A stored `YYYY-MM-DD` is built back up from its own digits,
 * so the day cannot shift with the viewer's timezone. And a value that is
 * neither is printed EXACTLY as the record holds it, flagged: this panel exists
 * to check a record against what was typed in, so a value we cannot read is
 * something the operator has to see — not something to hide behind a dash, and
 * not something to dress up as a date it might not be.
 */
function DateOfBirth(p: { value: string | null }) {
  if (p.value === null) return <Blank />
  const label = formatDob(p.value)
  if (label !== null) return <span className="text-[12.5px]">{label}</span>
  return (
    <Tooltip content="Not stored as YYYY-MM-DD, so it is shown exactly as the record holds it. Re-enter it on the edit screen.">
      <span className="tabular inline-flex cursor-default items-center gap-1 font-mono text-[12px] break-all text-warn">
        <AlertTriangle aria-hidden="true" className="size-3.5 flex-none" />
        {p.value}
      </span>
    </Tooltip>
  )
}

/** An id with a copy affordance. Used for every value an operator pastes elsewhere. */
function IdField(p: { label: string; value: string | null; copyValue?: string | null }) {
  const copy = p.copyValue ?? p.value
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-faint">{p.label}</dt>
      <dd className="mt-px flex min-w-0 items-center gap-0.5">
        {p.value === null ? (
          <Blank />
        ) : (
          <>
            <span className="tabular truncate font-mono text-[12px] text-fg">{p.value}</span>
            {copy ? <CopyButton value={copy} label={`Copy ${p.label.toLowerCase()}`} /> : null}
          </>
        )}
      </dd>
    </div>
  )
}

/**
 * The one field routinely longer than its half of the grid. It truncates
 * rather than wraps so the row stays a row, and the full address is a hover
 * away — the copy button is the real path off the screen either way.
 */
function EmailField(p: { value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-faint">Email</dt>
      <dd className="mt-px flex min-w-0 items-center gap-0.5">
        <Tooltip content={p.value}>
          <span className="cursor-default truncate text-[12.5px] text-fg">{p.value}</span>
        </Tooltip>
        <CopyButton value={p.value} label="Copy email" />
      </dd>
    </div>
  )
}

/**
 * The PIN, in the clear — one of the panel's two deliberate reversals; see the
 * note at the top of this file. Set a step heavier and wider-tracked than its
 * neighbours because it is the value most often read off this screen while
 * typing into another one, and six undifferentiated digits is where a person
 * loses their place.
 */
function PinField(p: { value: string | null }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-faint">PIN</dt>
      <dd className="mt-px flex min-w-0 items-center gap-0.5">
        {p.value === null ? (
          <Blank />
        ) : (
          <>
            <span className="tabular truncate font-mono text-[13px] font-semibold tracking-[0.14em] text-fg">
              {p.value}
            </span>
            <CopyButton value={p.value} label="Copy PIN" />
          </>
        )}
      </dd>
    </div>
  )
}

/**
 * pending → warn, approved → info, confirmed → ok: the badge answers "is
 * anyone still waiting on this record" at a glance instead of painting every
 * state the same grey. Unknown strings fall back to neutral rather than
 * crashing the panel over a value some other writer put in the column.
 */
const STATUS_TONE: Record<CustomerStatus, Tone> = {
  pending: 'warn',
  approved: 'info',
  confirmed: 'ok',
}

/**
 * The shift the customer wants, set right here.
 *
 * The one field on this panel that WRITES, and it does so on the same terms
 * as the table's in-place edits: the caches are patched first — this drawer,
 * and every cached page of the list, so the row's sun or moon changes in the
 * same frame as the pill — and the request follows. On failure the field is
 * put back exactly as it was and the operator is told.
 *
 * On success the LIST is re-asked, once. Other consoles need nothing: the
 * change stream carries `shift_priority`, so the value is pushed to them and
 * this one already has it. But a value is not the only thing the list shows —
 * with the toolbar's Shift filter set to Day, a customer just moved to Night
 * no longer belongs on the page at all, and a patch in place cannot know
 * that. One refetch of the pages on screen is what makes the filter true
 * again; without a Shift filter it returns the same rows and costs a query.
 *
 * Only `shiftPriority` is rolled back: a realtime patch may well have landed
 * on this customer while the request was in flight, and a wholesale restore
 * would undo that too.
 *
 * A second pick is refused while one is saving. Two writes racing for the
 * same column can land in either order, and the row would then show the
 * loser — a spinner for a few hundred milliseconds is the cheaper answer.
 */
function ShiftPriorityField(p: { id: string; customerId: string; value: ShiftPriority | null }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [saving, setSaving] = React.useState<ShiftPriority | null>(null)

  // The panel is keyed on the customer, so a save that outlives it must not
  // set state on an unmounted field — or clear a spinner that belongs to the
  // next customer's own save.
  const mounted = React.useRef(true)
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const { id, customerId, value } = p
  const pick = React.useCallback(
    async (next: ShiftPriority): Promise<void> => {
      if (saving !== null || next === value) return
      const previous = value
      setSaving(next)
      applyCustomerPatches(queryClient, new Map([[id, { shiftPriority: next }]]))

      try {
        await apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(id)}`, {
          shiftPriority: next,
        })
        void queryClient.invalidateQueries({ queryKey: ['customers'] })
      } catch (err) {
        applyCustomerPatches(queryClient, new Map([[id, { shiftPriority: previous }]]))
        toast({
          title: 'Shift priority not saved',
          description: `${customerId}: ${friendlyError(err)}`,
          tone: 'danger',
        })
      } finally {
        if (mounted.current) setSaving(null)
      }
    },
    [queryClient, toast, id, customerId, value, saving]
  )

  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-faint">Shift priority</dt>
      <dd className="mt-1 min-w-0">
        <ShiftPriorityPicker
          value={value}
          saving={saving}
          onPick={(next) => void pick(next)}
          aria-label={`Shift priority for ${customerId}`}
        />
      </dd>
    </div>
  )
}

function RelativeField(p: { label: string; iso: string | null }) {
  const relative = useRelativeTime(p.iso)
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-faint">{p.label}</dt>
      <dd className="mt-px min-w-0">
        <Tooltip content={formatAbsolute(p.iso)}>
          <span className="cursor-default text-[12.5px] text-fg">{relative}</span>
        </Tooltip>
      </dd>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Credential presence
 * ------------------------------------------------------------------ */

type CredentialKey = keyof CustomerDetail['has']

/**
 * Only the two secrets the browser never receives. The PIN and SIN used to
 * sit in this list as presence rows, but both are now shown in the clear in
 * Identity, and "PIN: Set" next to a visible PIN is a question the panel had
 * already answered.
 */
const CREDENTIALS: readonly {
  key: CredentialKey
  label: string
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
}[] = [
  { key: 'workAuth', label: 'Work auth', icon: ShieldCheck },
  { key: 'imapAppPassword', label: 'IMAP password', icon: Mail },
]

function CredentialRow(p: {
  label: string
  present: boolean
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
}) {
  const Icon = p.icon
  return (
    <div className="flex items-center gap-2 rounded-md border border-line bg-s2 px-2 py-1.5">
      <Icon aria-hidden="true" className="size-3.5 flex-none text-faint" />
      <span className="min-w-0 flex-1 truncate text-[12px] text-fg">{p.label}</span>
      {/* Shape carries the meaning as well as colour: a tick versus a dash reads
          correctly in greyscale and to a colour-blind operator. */}
      {p.present ? (
        <span className="flex items-center gap-1 text-[11px] font-medium text-ok">
          <Check aria-hidden="true" className="size-3.5" strokeWidth={3} />
          <span className="sr-only">Stored</span>
          <span aria-hidden="true">Set</span>
        </span>
      ) : (
        <span className="flex items-center gap-1 text-[11px] text-faint">
          <Minus aria-hidden="true" className="size-3.5" strokeWidth={3} />
          <span className="sr-only">Not stored</span>
          <span aria-hidden="true">None</span>
        </span>
      )}
    </div>
  )
}

/*
 * The session-history list and its row lived here.
 *
 * They rendered `customer_sessions` — the SIBLING fleet's record of its own
 * runs — in the same shape as everything else on this panel, with no way to
 * tell whose work you were looking at. This console keeps its own record of
 * every run it performs; open the run drawer for that.
 */

/* ------------------------------------------------------------------ *
 * Verification code — "Get OTP"
 * ------------------------------------------------------------------ */

/**
 * Amazon's codes do not carry an expiry we can read, so this is the point at
 * which the panel stops presenting one as current and starts saying it may
 * already be dead.
 *
 * Three minutes, deliberately shorter than the server's ten-minute read window:
 * the window decides what is worth SHOWING somebody who is on the phone to a
 * customer, and this decides what is worth them TYPING without checking. A code
 * shown as fresh that turns out to be expired costs a failed sign-in attempt
 * against a rate-limited service; a code shown as old that still works costs a
 * glance.
 */
const CODE_FRESH_MS = 180_000

/** Copy confirmation, matched to `CopyButton` so the two do not blink apart. */
const COPIED_MS = 1_600

/**
 * `123 456`.
 *
 * The code is read aloud down a phone line far more often than it is pasted, and
 * six unbroken digits is where a person loses their place. Only ever a visual
 * grouping: everything that copies, compares or announces uses the raw string.
 */
function groupCode(code: string): string {
  return /^\d{6}$/.test(code) ? `${code.slice(0, 3)} ${code.slice(3)}` : code
}

/** Title and tone per failure. The server owns the sentence underneath. */
const MISS: Record<OtpFetchReason, { title: string; tone: 'warn' | 'danger' }> = {
  none: { title: 'No code has arrived yet', tone: 'warn' },
  imap_auth: { title: 'Gmail refused the app password', tone: 'danger' },
  imap_connect: { title: 'Could not reach the inbox', tone: 'warn' },
  aborted: { title: 'The read was cancelled', tone: 'warn' },
  internal: { title: 'The read did not finish', tone: 'danger' },
}

/**
 * The verification code sitting in this customer's Gmail, on demand.
 *
 * ── What pressing it does, and does not do ──────────────────────────
 * The server opens the mailbox with the app password already on the record,
 * scans it once read-only and closes. NOTHING is sent to Amazon and no new code
 * is triggered, so this cannot cost the customer an attempt and cannot race a
 * sign-in that is running for them at the same moment. That is why the button
 * stays live at every status and why "read again" is offered without ceremony.
 *
 * ── Why the code is copied without being asked ──────────────────────
 * Because the clipboard is where it was going next either way — the operator is
 * mid-call, and the alternative is transcribing six digits by eye. The copy is
 * still a real button whose label only ever claims what the clipboard actually
 * accepted: outside a secure context the write silently fails, and a card that
 * said "copied" would be lying at the worst possible moment.
 *
 * ── Why the age is the server's number ──────────────────────────────
 * Only the server saw both its own clock and the mail server's. This ticks that
 * measurement forward on the shared app clock rather than re-deriving an age
 * from a timestamp against a laptop that may be minutes out.
 */
function VerificationCode(p: { customerId: string; hasAppPassword: boolean; email: string }) {
  const otp = useFetchOtp()
  const [result, setResult] = React.useState<{ res: OtpFetchResponse; readAtMs: number } | null>(
    null
  )
  const [copied, setCopied] = React.useState(false)
  const [stale, setStale] = React.useState(false)
  const copyTimer = React.useRef<number | null>(null)

  React.useEffect(() => {
    return () => {
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current)
    }
  }, [])

  const copy = React.useCallback(async (code: string) => {
    const ok = await writeClipboard(code)
    if (!ok) return
    setCopied(true)
    if (copyTimer.current !== null) window.clearTimeout(copyTimer.current)
    copyTimer.current = window.setTimeout(() => {
      setCopied(false)
      copyTimer.current = null
    }, COPIED_MS)
  }, [])

  const hit = result !== null && result.res.ok && result.res.code !== null ? result : null
  /*
   * When the mail actually arrived, on THIS browser's clock.
   *
   * The server's age at the instant of the read, subtracted from the instant the
   * answer landed here — so the stopwatch below counts from the real arrival
   * without ever trusting this machine's clock against Google's.
   */
  const arrivedAtMs = hit === null ? null : hit.readAtMs - (hit.res.ageMs ?? 0)
  const age = useLiveElapsed(arrivedAtMs)

  /*
   * One timer that fires once, rather than a tick that re-evaluates a threshold.
   * The moment the code crosses out of "fresh" is known the instant it arrives,
   * so it is scheduled, not polled.
   */
  React.useEffect(() => {
    if (arrivedAtMs === null) {
      setStale(false)
      return
    }
    const remaining = CODE_FRESH_MS - (Date.now() - arrivedAtMs)
    if (remaining <= 0) {
      setStale(true)
      return
    }
    setStale(false)
    const timer = window.setTimeout(() => setStale(true), remaining)
    return () => window.clearTimeout(timer)
  }, [arrivedAtMs])

  const run = (): void => {
    if (otp.isPending) return
    // Clear first: while a re-read is in flight, the previous code is an answer
    // to a question nobody is asking any more, and leaving it on screen next to
    // a spinner is how the old one gets read out.
    setResult(null)
    setCopied(false)
    otp.mutate(p.customerId, {
      onSuccess: (res) => {
        setResult({ res, readAtMs: Date.now() })
        if (res.ok && res.code !== null) void copy(res.code)
      },
    })
  }

  /* -------- the request itself failed (404 / 400 / 429 / network) ---- */
  const requestError =
    otp.isError && result === null
      ? otp.error instanceof ApiError
        ? otp.error.message
        : 'The request did not reach the server. Check the connection and try again.'
      : null

  const miss = result !== null && !result.res.ok ? MISS[result.res.reason ?? 'internal'] : null

  const trigger = (
    <Button
      variant={hit === null ? 'primary' : 'secondary'}
      size="md"
      onClick={run}
      loading={otp.isPending}
      disabled={!p.hasAppPassword}
      className="max-md:h-11 max-md:text-[14px]"
    >
      {otp.isPending ? null : <KeyRound aria-hidden="true" />}
      {otp.isPending ? 'Reading inbox…' : hit === null ? 'Get OTP' : 'Read again'}
    </Button>
  )

  return (
    <Section title="Verification code" icon={<KeyRound />}>
      <div className="flex items-start gap-3">
        {p.hasAppPassword ? (
          trigger
        ) : (
          <Tooltip content="No Gmail app password is stored for this customer, so their inbox cannot be opened.">
            {/* A disabled button is not a hover target; the span is what the
                tooltip can attach to. */}
            <span className="inline-block">{trigger}</span>
          </Tooltip>
        )}

        <p className="min-w-0 flex-1 text-[11.5px] leading-[1.45] text-faint">
          Reads the newest code from{' '}
          <span className="break-all text-muted">{p.email}</span>. Nothing is sent to Amazon and no
          new code is triggered.
        </p>
      </div>

      {/* Mounted for the life of the panel so a screen reader is already
          watching this region when a code lands in it — a live region that
          appears already populated is frequently never announced. */}
      <div role="status" aria-live="polite" className="empty:hidden">
        {hit !== null && hit.res.code !== null ? (
          <div
            className={cn(
              'mt-3 rounded-md border px-3 py-2.5',
              stale ? 'border-warn/30 bg-warn-soft' : 'border-ok/30 bg-ok-soft'
            )}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="tabular font-mono text-[26px] leading-none font-semibold tracking-[0.18em] text-fg select-all">
                {/* Two renderings of one code: grouped for the eye, spelled out
                    for the ear. A screen reader given "417293" says "four
                    hundred and seventeen thousand…", which is unusable for
                    someone reading it down a phone line. */}
                <span aria-hidden="true">{groupCode(hit.res.code)}</span>
                <span className="sr-only">
                  Verification code {hit.res.code.split('').join(' ')}
                </span>
              </p>

              <div className="ml-auto flex items-center gap-1.5">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void copy(hit.res.code as string)}
                  className={cn(copied && 'border-ok/40 text-ok')}
                >
                  {copied ? <Check aria-hidden="true" strokeWidth={3} /> : <Copy aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Tooltip content="Read the inbox again">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label="Read the inbox again"
                    onClick={run}
                    loading={otp.isPending}
                  >
                    <RotateCw aria-hidden="true" />
                  </Button>
                </Tooltip>
              </div>
            </div>

            <p className="mt-1.5 text-[11.5px] leading-[1.5] text-muted">
              Arrived {age} ago
              {hit.res.mailbox !== null && hit.res.mailbox.toUpperCase() !== 'INBOX' ? (
                <>
                  {' · '}
                  <span className="text-warn">found in {hit.res.mailbox}</span>
                </>
              ) : null}
              {stale ? (
                <>
                  {' · '}
                  <span className="text-warn">
                    older than {Math.round(CODE_FRESH_MS / 60_000)} minutes, so it may already have
                    expired
                  </span>
                </>
              ) : null}
            </p>
          </div>
        ) : miss !== null && result !== null ? (
          <div
            className={cn(
              'mt-3 flex items-start gap-2.5 rounded-md border px-3 py-2.5',
              miss.tone === 'danger' ? 'border-danger/30 bg-danger-soft' : 'border-warn/30 bg-warn-soft'
            )}
          >
            <AlertTriangle
              aria-hidden="true"
              className={cn(
                'mt-px size-4 flex-none',
                miss.tone === 'danger' ? 'text-danger' : 'text-warn'
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-medium text-fg">{miss.title}</p>
              {/* The server's own sentence, verbatim — it is the only party that
                  knows whether Amazon sent anything at all, and it says so. */}
              <p className="mt-0.5 text-[12px] leading-[1.5] text-muted">{result.res.message}</p>
            </div>
          </div>
        ) : requestError !== null ? (
          <div className="mt-3 flex items-start gap-2.5 rounded-md border border-danger/30 bg-danger-soft px-3 py-2.5">
            <AlertTriangle aria-hidden="true" className="mt-px size-4 flex-none text-danger" />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-medium text-fg">Could not read the code</p>
              <p className="mt-0.5 text-[12px] leading-[1.5] break-words text-muted">
                {requestError}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ *
 * Assessment — the questions hand-off
 * ------------------------------------------------------------------ */

/**
 * Shown only for a customer whose audio assessment COMPLETED: the one state
 * in which there is a finished set of questions worth handing back. A running,
 * stopped or failed run shows nothing here rather than a card that opens on a
 * partial record.
 *
 * The dialog is a child of the section, so its open state lives and dies with
 * the customer it belongs to — a switch to another customer unmounts it — and
 * it is forced shut the moment the status is no longer `completed` (a re-run
 * begun while it was open), so it can never sit over a record that no longer
 * describes what the customer sat.
 */
function AssessmentSection(p: {
  customerId: string
  name: string
  phone: string | null
  completed: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const onOpen = React.useCallback(() => setOpen(true), [])

  if (!p.completed) return null

  return (
    <Section title="Assessment" icon={<ClipboardList />}>
      <AssessmentCard customerId={p.customerId} onOpen={onOpen} />
      <AssessmentDialog
        customerId={p.customerId}
        customerName={p.name}
        phone={p.phone}
        open={open}
        onOpenChange={setOpen}
      />
    </Section>
  )
}

/* ------------------------------------------------------------------ *
 * Secured shifts
 * ------------------------------------------------------------------ */

/**
 * The shifts this customer actually got all the way through.
 *
 * Distinct from the claim the Won rail shows, and the difference is the point:
 * a claim is a soft seat held for a few hours with the application still to
 * file, and it is overwritten by the next one. Each entry here is a run that
 * reached `supplementary-success` — the whole form in, the workflow closed by
 * Amazon — and they accumulate, so a customer run three times has three.
 *
 * Collapsed by default. Open, it is the fullest record of one moment anywhere
 * in this console, and shut it is one line saying how many there are and when
 * the last one landed — which is what somebody scrolling a panel for something
 * else actually wants from it. The section renders nothing at all when there
 * are none, because "no secured shifts" is the state of almost every customer
 * and an empty box on every panel is worse than no box.
 */
function SecuredShiftsSection(p: { shifts: readonly SecuredShiftDto[] }) {
  const [open, setOpen] = React.useState(false)

  /*
   * Newest first, and copied before reversing.
   *
   * The API sends them oldest first, as `$push` stored them — a faithful copy of
   * the document. Reading order is the other one: the shift somebody opened this
   * panel for is the last one that happened. `reverse` mutates, so the copy is
   * load-bearing; reversing the array React Query is caching would corrupt the
   * cache for every other reader of it.
   */
  const newestFirst = React.useMemo(() => [...p.shifts].reverse(), [p.shifts])
  const newest = newestFirst[0]

  if (newest === undefined) return null

  const count = newestFirst.length

  return (
    <Section title="Secured shifts" icon={<CalendarCheck />}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border border-line bg-s2 px-2.5 py-2 text-left',
          'transition-colors hover:border-line-strong hover:bg-inset',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
        )}
      >
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'size-3.5 flex-none text-faint transition-transform',
            open ? 'rotate-0' : '-rotate-90'
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] font-medium text-fg">
            {count} secured shift{count === 1 ? '' : 's'}
          </span>
          {/* The summary is the NEWEST entry, not an aggregate: "latest" is the
              question a collapsed row can answer honestly in one line. */}
          <span className="mt-px block truncate text-[11.5px] text-faint">
            Latest: {newest.siteId ?? '—'}
            {newest.city ? ` · ${newest.city}` : ''}
            {newest.appointmentDate ? ` · ${newest.appointmentDate}` : ' · no orientation booked'}
          </span>
        </span>
        <span className="flex-none text-[11px] text-faint">{open ? 'Hide' : 'Show all'}</span>
      </button>

      {open ? (
        <ul className="mt-2 space-y-2">
          {newestFirst.map((shift) => (
            /*
             * Keyed on the run, not the application id. Amazon hands the same
             * application back when one is already open for a candidate, so two
             * genuine entries can share an `applicationId` — which would be a
             * duplicate React key and a silently dropped row. The run id is
             * unique per run by construction; the secured instant disambiguates
             * the rows written before it existed.
             */
            <SecuredShiftCard key={`${shift.runId ?? shift.applicationId}:${shift.securedAt}`} shift={shift} />
          ))}
        </ul>
      ) : null}
    </Section>
  )
}

/** One secured shift, expanded. */
function SecuredShiftCard(p: { shift: SecuredShiftDto }) {
  const shift = p.shift
  const relative = useRelativeTime(shift.securedAt)

  const location = [shift.city, shift.state].filter((v): v is string => v !== null).join(', ')

  return (
    <li className="rounded-md border border-line bg-s2 px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="tabular truncate font-mono text-[12px] font-medium text-fg">
            {shift.siteId ?? '—'}
          </p>
          {location === '' ? null : (
            <p className="mt-px truncate text-[11.5px] text-muted">{location}</p>
          )}
        </div>
        {shift.scheduleType === null ? null : (
          <Badge tone="info">{shift.scheduleType}</Badge>
        )}
      </div>

      {/*
        The orientation. All three fields are null together when the run filed
        the application without booking one — a real outcome, and one worth
        saying out loud rather than rendering as an empty row: somebody has to
        book it by hand.

        `appointmentDate` is the site's own rendering and carries NO year, so the
        raw `DD/MM/YYYY` value rides in the tooltip. It is never parsed on
        either side — day-first dates are how the 3rd of April becomes the 4th
        of March.
      */}
      <div className="mt-2 border-t border-line pt-2">
        {shift.appointmentDate === null && shift.appointmentTime === null ? (
          <p className="text-[11.5px] text-warn">No orientation was booked on this run.</p>
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] text-faint">Orientation</span>
            {shift.appointmentDateRaw === null ? (
              <span className="text-[12.5px] text-fg">
                {shift.appointmentDate ?? '—'} {shift.appointmentTime ?? ''}
              </span>
            ) : (
              <Tooltip content={`${shift.appointmentDateRaw} (DD/MM/YYYY)`}>
                <span className="cursor-default text-[12.5px] text-fg">
                  {shift.appointmentDate ?? '—'} {shift.appointmentTime ?? ''}
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-line pt-2">
        <IdField
          label="Application"
          value={shift.applicationId}
          copyValue={shift.applicationId}
        />
        <div className="min-w-0">
          <dt className="text-[11px] text-faint">Secured</dt>
          <dd className="mt-px min-w-0">
            <Tooltip content={formatAbsolute(shift.securedAt)}>
              <span className="cursor-default text-[12.5px] text-fg">{relative}</span>
            </Tooltip>
          </dd>
        </div>
        <IdField label="Job" value={shift.jobIdFull ?? shift.jobId} copyValue={shift.jobIdFull} />
        <IdField
          label="Schedule"
          value={shift.scheduleIdFull ?? shift.scheduleId}
          copyValue={shift.scheduleIdFull}
        />
      </dl>
    </li>
  )
}

/* ------------------------------------------------------------------ *
 * Body
 * ------------------------------------------------------------------ */

function DetailSkeleton() {
  return (
    <div className="space-y-4 px-4 py-4" aria-busy="true">
      <Skeleton className="h-4 w-40" />
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-3.5 w-28" />
          </div>
        ))}
      </div>
      <Skeleton className="h-16 w-full rounded-md" />
      <Skeleton className="h-16 w-full rounded-md" />
    </div>
  )
}

function DetailBody(p: { detail: CustomerDetail }) {
  const detail = p.detail

  const warehouses = React.useMemo(
    () => Object.entries(detail.cityWarehouses ?? {}),
    [detail.cityWarehouses]
  )

  const addressLines = [
    detail.addressLine1,
    detail.addressLine2,
    [detail.addressCity, detail.addressState, detail.addressZipcode].filter(Boolean).join(', '),
  ].filter((line): line is string => typeof line === 'string' && line.trim() !== '')

  return (
    <div>
      {/* First, above identity: it is the one thing on this panel that is an
          ACTION rather than a record, and it is opened for mid-call. Keyed on
          the customer so a code read for one person cannot survive into another
          person's panel when the same panel is reused. */}
      <VerificationCode
        key={detail.customerId}
        customerId={detail.customerId}
        hasAppPassword={detail.has.imapAppPassword}
        email={detail.email}
      />

      {/* The second action, and the only other one: hand a finished
          assessment's questions back to the customer. Keyed like the code
          reader, for the same reason. */}
      <AssessmentSection
        key={`assessment:${detail.customerId}`}
        customerId={detail.customerId}
        name={detail.name}
        phone={detail.phone}
        completed={detail.assessmentStatus === 'completed'}
      />

      {/* Ordered as a handover is read out: how to reach them, what they sign
          in with, who they are, then the record's own bookkeeping. Mode and
          the raw row id earned nothing here — one is a fleet setting the
          table already facets on, the other a database detail nobody types. */}
      <Section title="Identity" icon={<IdCard />}>
        <Grid>
          <EmailField value={detail.email} />
          <PinField value={detail.pin} />
          <Field label="Name">
            <Text value={detail.name} />
          </Field>
          <Field label="Status">
            <Badge tone={STATUS_TONE[detail.status] ?? 'neutral'}>{detail.status}</Badge>
          </Field>
          <IdField label="Customer ID" value={detail.customerId} />
          <Field label="Primary contact">
            {detail.isPrimary ? (
              <Badge tone="accent">Primary</Badge>
            ) : (
              <span className="text-muted">Secondary</span>
            )}
          </Field>
          <Field label="Agent">
            {detail.agentUsername === null ? (
              <Blank />
            ) : (
              <span>
                {detail.agentName ?? detail.agentUsername}
                <span className="ml-1 font-mono text-[11px] text-faint">
                  @{detail.agentUsername}
                </span>
              </span>
            )}
          </Field>
          <Field label="Computer">
            <Text value={detail.computerNumber} mono />
          </Field>
          <Field label="Date of birth">
            <DateOfBirth value={detail.dateOfBirth} />
          </Field>
          {/* In the clear on purpose, like the PIN above — see the note at the
              top of this file and on the DTO. */}
          <IdField label="SIN number" value={detail.sinNumber} />
        </Grid>
      </Section>

      <Section title="Contact" icon={<Phone />}>
        <Grid>
          <IdField label="Phone" value={detail.phone} />
          {/* Keyed on the customer, like the code reader above: a save in
              flight for one person must not leave its spinner on the next. */}
          <ShiftPriorityField
            key={`shift:${detail.customerId}`}
            id={detail.id}
            customerId={detail.customerId}
            value={detail.shiftPriority}
          />
        </Grid>
      </Section>

      <Section title="Address" icon={<MapPin />}>
        {addressLines.length === 0 ? (
          <p className="text-[12.5px] text-faint">No address on file.</p>
        ) : (
          <address className="text-[12.5px] leading-[1.6] text-fg not-italic">
            {addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </address>
        )}
      </Section>

      <Section title="Cities and warehouses" icon={<Building2 />}>
        {warehouses.length === 0 ? (
          detail.cities.length === 0 ? (
            <p className="text-[12.5px] text-faint">No cities selected.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {detail.cities.map((city) => (
                <Badge key={city} tone="neutral">
                  {formatCity(city)}
                </Badge>
              ))}
            </div>
          )
        ) : (
          <ul className="space-y-2">
            {warehouses.map(([city, sites]) => (
              <li key={city} className="rounded-md border border-line bg-s2 px-2.5 py-2">
                <p className="text-[12.5px] font-medium text-fg">{formatCity(city)}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {sites.length === 0 ? (
                    <span className="text-[11.5px] text-faint">No sites</span>
                  ) : (
                    sites.map((site) => (
                      <span
                        key={site}
                        className="rounded-xs bg-inset px-1.5 py-px font-mono text-[11px] text-muted"
                      >
                        {site}
                      </span>
                    ))
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* The shift priority that qualifies these hours is set in Contact,
            beside the phone — the one editable field on this panel. */}
        {detail.workHours.length > 0 ? (
          <div className="mt-3">
            <p className="mb-1.5 text-[11px] text-faint">Work hours</p>
            <div className="flex flex-wrap gap-1.5">
              {detail.workHours.map((hours) => (
                <Badge key={hours} tone="info">
                  {hours}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
      </Section>

      {/* Below Work hours and above the CURRENT assignment, which is the order
          they read in: what this customer already got, then what they are
          pointed at now. Renders nothing when there are none. */}
      <SecuredShiftsSection shifts={detail.securedShifts} />

      <Section title="Job and schedule" icon={<Briefcase />}>
        <Grid>
          <IdField label="Job" value={detail.jobIdFull ?? detail.jobId} copyValue={detail.jobIdFull} />
          <IdField
            label="Schedule"
            value={detail.scheduleIdFull ?? detail.scheduleId}
            copyValue={detail.scheduleIdFull}
          />
          {/*
            The UUID form, and this is the one screen it belongs on: two
            40-character strings are worth showing for one customer opened on
            purpose, and not worth carrying on every row of the table.

            Rendered only when there is a pair to talk about, so an unassigned
            customer's panel is unchanged. When the pair exists but the UUID does
            not, the field still renders and says so — an empty space would read
            as "not loaded" rather than as the answer.
          */}
          {detail.targetUuid === null ? null : (
            <>
              <IdField label="Job UUID" value={detail.jobUuid} copyValue={detail.jobUuid} />
              <IdField
                label="Shift UUID"
                value={
                  detail.scheduleUuid ??
                  (detail.targetUuid === 'job_only' ? 'Not resolved yet' : 'Not on file')
                }
                copyValue={detail.scheduleUuid}
              />
            </>
          )}
        </Grid>
      </Section>

      {/* The other project's fields, shown as reference and labelled as such.
          Nothing here decides this customer's status in this console — see
          `liveStateOf` in @acs/shared. */}
      {/* The sibling fleet's session id and heartbeat were shown here. They
          are no longer read from the database at all — what this console is
          doing for a customer is in the run drawer. */}
      <Section title="Record" icon={<Clock />}>
        <Grid>
          <RelativeField label="Created" iso={detail.createdAt} />
          <RelativeField label="Updated" iso={detail.updatedAt} />
        </Grid>
      </Section>

      <Section title="Credentials on file" icon={<ShieldCheck />}>
        <div className="grid grid-cols-2 gap-1.5">
          {CREDENTIALS.map((credential) => (
            <CredentialRow
              key={credential.key}
              label={credential.label}
              icon={credential.icon}
              present={detail.has[credential.key]}
            />
          ))}
        </div>
        <p className="mt-2 text-[11px] leading-[1.5] text-faint">
          Presence only. These values are never read out of the database and never reach this
          browser.
        </p>
      </Section>

      {detail.notes ? (
        <Section title="Notes" icon={<ScrollText />}>
          <p className="text-[12.5px] leading-[1.6] whitespace-pre-wrap text-fg">{detail.notes}</p>
        </Section>
      ) : null}

    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Panel
 * ------------------------------------------------------------------ */

export function CustomerDetailPanel(p: { customerId: string | null; onClose: () => void }) {
  const detail = useCustomerDetail(p.customerId)
  const onClose = p.onClose

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) onClose()
    },
    [onClose]
  )

  const data = detail.data

  return (
    <Drawer
      open={p.customerId !== null}
      onOpenChange={onOpenChange}
      // `md:`-prefixed like every other caller: below md the Drawer is
      // full-screen, and an unprefixed `w-[600px]` only avoids overflowing a
      // 360px phone because the Drawer's `max-md:w-full` seatbelt happens to
      // be emitted later in the stylesheet. Saying `md:` states the intent
      // instead of leaning on utility ordering.
      widthClass="md:w-[600px]"
      title={data ? data.name || data.customerId : 'Customer'}
      description={
        data ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono text-[11.5px]">{data.customerId}</span>
            <span aria-hidden="true" className="text-faint">
              ·
            </span>
            <span className="truncate">{data.email}</span>
          </span>
        ) : (
          'Loading customer…'
        )
      }
      /* No footer action. "View session log" opened the sibling fleet's log for
         this customer's legacy `sessionId`; runs performed here are read in the
         run drawer, which the table row opens. */
    >
      {detail.isPending ? (
        <DetailSkeleton />
      ) : detail.isError ? (
        <EmptyState
          icon={<AlertTriangle />}
          title="Could not load this customer"
          description={
            detail.error instanceof Error ? detail.error.message : 'The request failed.'
          }
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void detail.refetch()}
              loading={detail.isFetching}
            >
              <RefreshCw aria-hidden="true" />
              Retry
            </Button>
          }
        />
      ) : data ? (
        <DetailBody detail={data} />
      ) : null}
    </Drawer>
  )
}
